// src/services/ociMonitoringApi.js
import api from './api';

/**
 * OCI Monitoring API Service
 * Provides methods to interact with OCI Monitoring endpoints
 */
const ociMonitoringApi = {
  /**
   * Get all regions
   * @returns {Promise} Promise with regions data
   */
  getRegions: async () => {
    try {
      // Debug the API call
      console.log('Fetching regions from: /api/oci/regions');
      const response = await api.get('/api/oci/regions');
      console.log('Regions response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching regions:', error);
      throw error;
    }
  },

  /**
   * Get compartments, optionally filtered by region
   * @param {string} region - Optional region filter
   * @returns {Promise} Promise with compartments data
   */
  getCompartments: async (region = '') => {
    try {
      // Updated to use the correct API path
      const url = region ? `/api/oci/compartments?region=${region}` : '/api/oci/compartments';
      console.log('Fetching compartments from:', url);
      const response = await api.get(url);
      console.log('Compartments response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching compartments:', error);
      throw error;
    }
  },

  /**
   * Get OCI instances
   * @param {Object} params - Query parameters
   * @param {string} params.region - Optional region filter
   * @param {string} params.compartment - Optional compartment filter
   * @param {string} params.startDate - Optional start date (YYYY-MM-DD)
   * @param {string} params.endDate - Optional end date (YYYY-MM-DD)
   * @returns {Promise} Promise with instances data
   */
  getInstances: async (params = {}) => {
    try {
      const { region, compartment, startDate, endDate } = params;
      
      // Build query string with proper date format
      let url = '/api/oci/instances?';
      if (region) url += `region=${region}&`;
      if (compartment) url += `compartment=${compartment}&`;
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;
      
      console.log('Fetching instances from:', url);
      const response = await api.get(url);
      console.log('Instances response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching instances:', error);
      throw error;
    }
  },

  /**
   * Get OCI cost data
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.region - Optional region filter
   * @param {string} params.compartment - Optional compartment filter
   * @returns {Promise} Promise with cost data
   */
  getCosts: async (options = {}) => {
    try {
      console.log('API Call: getCosts with options:', options);
      
      const { startDate, endDate, region, compartment } = options;
      
      if (!startDate || !endDate) {
        throw new Error('Start date and end date are required');
      }
      
      // Try using the oci_costs endpoint first
      let url = `/api/oci/oci_costs?start_date=${startDate}&end_date=${endDate}`;
      if (region) url += `&region=${region}`;
      if (compartment) url += `&compartment=${compartment}`;
      
      console.log('Fetching cost data from:', url);
      
      try {
        const response = await api.get(url);
        console.log('Cost data from oci_costs endpoint:', response.data);
        
        // Ensure the data has region and compartment information
        let processedData = response.data;
        if (Array.isArray(processedData)) {
          processedData = processedData.map(day => ({
            ...day,
            region_name: day.region_name || day.region || region || 'All Regions',
            compartment_name: day.compartment_name || day.compartment || compartment || 'All Compartments'
          }));
        }
        
        return { data: processedData };
      } catch (ociCostsError) {
        console.warn('Error fetching from oci_costs, trying costs endpoint:', ociCostsError);
        
        // If oci_costs fails, try the costs endpoint as fallback
        url = `/api/oci/costs?start_date=${startDate}&end_date=${endDate}`;
        if (region) url += `&region=${region}`;
        if (compartment) url += `&compartment=${compartment}`;
        
        console.log('Trying fallback costs endpoint:', url);
        
        const fallbackResponse = await api.get(url);
        console.log('Cost data from costs endpoint:', fallbackResponse.data);
        
        // Ensure the data has region and compartment information
        let processedData = fallbackResponse.data;
        if (Array.isArray(processedData)) {
          processedData = processedData.map(day => ({
            ...day,
            region_name: day.region_name || day.region || region || 'All Regions',
            compartment_name: day.compartment_name || day.compartment || compartment || 'All Compartments'
          }));
        }
        
        return { data: processedData };
      }
    } catch (error) {
      console.error('Error fetching costs (all attempts failed):', error);
      throw error;
    }
  },

  /**
   * Get metrics trend data
   * @param {Object} params - Query parameters
   * @param {string} params.metricType - Type of metric (instances, cpu, memory, boot_volume, block_volume)
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.region - Optional region filter
   * @param {string} params.compartment - Optional compartment filter
   * @returns {Promise} Promise with metric trend data
   */
  getMetricTrend: async (params = {}) => {
    try {
      const { metricType, startDate, endDate, region, compartment } = params;
      
      if (!metricType || !startDate || !endDate) {
        throw new Error('Metric type, start date, and end date are required');
      }
      
      // Updated to use the correct API path
      let url = `/api/oci/metrics/trend?metric_type=${metricType}&start_date=${startDate}&end_date=${endDate}`;
      if (region) url += `&region=${region}`;
      if (compartment) url += `&compartment=${compartment}`;
      
      console.log(`Fetching ${metricType} metric data from:`, url);
      const response = await api.get(url);
      console.log(`${metricType} metric response:`, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching metric trend:', error);
      throw error;
    }
  },

  /**
   * Get OCI summary data
   * @param {Object} params - Query parameters
   * @param {string} params.date - Optional date (YYYY-MM-DD)
   * @param {string} params.region - Optional region filter
   * @param {string} params.compartment - Optional compartment filter
   * @returns {Promise} Promise with summary data
   */
  getSummary: async (params = {}) => {
    try {
      const { date, region, compartment } = params;
      
      // Updated to use the correct API path
      let url = '/api/oci/summary?';
      if (date) url += `date=${date}&`;
      if (region) url += `region=${region}&`;
      if (compartment) url += `compartment=${compartment}&`;
      
      console.log('Fetching summary from:', url);
      const response = await api.get(url);
      console.log('Summary response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    }
  },

  /**
   * Get storage details
   * @param {Object} params - Query parameters
   * @param {string} params.region - Optional region filter
   * @param {string} params.compartment - Optional compartment filter
   * @param {string} params.date - Optional date (YYYY-MM-DD)
   * @returns {Promise} Promise with storage details
   */
  getStorageDetails: async (params = {}) => {
    try {
      const { region, compartment, date } = params;
      
      // Updated to use the correct API path
      let url = '/api/oci/storage/details?';
      if (region) url += `region=${region}&`;
      if (compartment) url += `compartment=${compartment}&`;
      if (date) url += `date=${date}`;
      
      console.log('Fetching storage details from:', url);
      const response = await api.get(url);
      console.log('Storage details response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching storage details:', error);
      throw error;
    }
  },

  /**
   * Get storage trend data
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.region - Optional region filter
   * @param {string} params.compartment - Optional compartment filter
   * @returns {Promise} Promise with storage trend data
   */
  getStorageTrends: async (params = {}) => {
    try {
      const { startDate, endDate, region, compartment } = params;
      
      if (!startDate || !endDate) {
        throw new Error('Start date and end date are required');
      }
      
      // Updated to use the correct API path
      let url = `/api/oci/storage/trends?start_date=${startDate}&end_date=${endDate}`;
      if (region) url += `&region=${region}`;
      if (compartment) url += `&compartment=${compartment}`;
      
      console.log('Fetching storage trends from:', url);
      const response = await api.get(url);
      console.log('Storage trends response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching storage trends:', error);
      throw error;
    }
  },

  /**
   * Get storage cost data
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.region - Optional region filter
   * @param {string} params.compartment - Optional compartment filter
   * @returns {Promise} Promise with storage cost data
   */
  getStorageCosts: async (params = {}) => {
    try {
      const { startDate, endDate, region, compartment } = params;
      
      if (!startDate || !endDate) {
        throw new Error('Start date and end date are required');
      }
      
      // Updated to use the correct API path
      let url = `/api/oci/storage/costs?start_date=${startDate}&end_date=${endDate}`;
      if (region) url += `&region=${region}`;
      if (compartment) url += `&compartment=${compartment}`;
      
      console.log('Fetching storage costs from:', url);
      const response = await api.get(url);
      console.log('Storage costs response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching storage costs:', error);
      throw error;
    }
  },

  /**
   * Get compartment tree
   * @param {string} region - Optional region filter
   * @returns {Promise} Promise with compartment tree data
   */
  getCompartmentTree: async (region = '') => {
    try {
      // Updated to use the correct API path
      const url = region ? `/api/oci/compartments/tree?region=${region}` : '/api/oci/compartments/tree';
      console.log('Fetching compartment tree from:', url);
      const response = await api.get(url);
      console.log('Compartment tree response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching compartment tree:', error);
      throw error;
    }
  },

  /**
   * Get compartment resources
   * @param {Object} params - Query parameters
   * @param {string} params.region - Optional region filter
   * @param {string} params.date - Optional date (YYYY-MM-DD)
   * @returns {Promise} Promise with compartment resources data
   */
  getCompartmentResources: async (params = {}) => {
    try {
      const { region, date } = params;
      
      // Updated to use the correct API path
      let url = '/api/oci/compartments/resources?';
      if (region) url += `region=${region}&`;
      if (date) url += `date=${date}`;
      
      console.log('Fetching compartment resources from:', url);
      const response = await api.get(url);
      console.log('Compartment resources response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching compartment resources:', error);
      throw error;
    }
  },

  /**
   * Get compartment costs
   * @param {Object} params - Query parameters
   * @param {string} params.region - Optional region filter
   * @param {string} params.date - Optional date (YYYY-MM-DD)
   * @returns {Promise} Promise with compartment costs data
   */
  getCompartmentCosts: async (params = {}) => {
    try {
      const { region, date } = params;
      
      // Updated to use the correct API path
      let url = '/api/oci/compartments/costs?';
      if (region) url += `region=${region}&`;
      if (date) url += `date=${date}`;
      
      console.log('Fetching compartment costs from:', url);
      const response = await api.get(url);
      console.log('Compartment costs response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching compartment costs:', error);
      throw error;
    }
  },

  /**
   * Get VM shape analysis
   * @param {Object} params - Query parameters
   * @param {string} params.region - Optional region filter
   * @param {string} params.compartment - Optional compartment filter
   * @param {string} params.date - Optional date (YYYY-MM-DD)
   * @returns {Promise} Promise with shape analysis data
   */
  getShapeAnalysis: async (params = {}) => {
    try {
      const { region, compartment, date } = params;
      
      // Updated to use the correct API path
      let url = '/api/oci/shapes/analysis?';
      if (region) url += `region=${region}&`;
      if (compartment) url += `compartment=${compartment}&`;
      if (date) url += `date=${date}`;
      
      console.log('Fetching shape analysis from:', url);
      const response = await api.get(url);
      console.log('Shape analysis response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching shape analysis:', error);
      throw error;
    }
  },

  /**
   * Get VM shape optimization recommendations
   * @param {Object} params - Query parameters
   * @param {string} params.region - Optional region filter
   * @param {string} params.compartment - Optional compartment filter
   * @param {string} params.date - Optional date (YYYY-MM-DD)
   * @returns {Promise} Promise with shape optimization data
   */
  getShapeOptimizations: async (params = {}) => {
    try {
      const { region, compartment, date } = params;
      
      // Updated to use the correct API path
      let url = '/api/oci/shapes/optimizations?';
      if (region) url += `region=${region}&`;
      if (compartment) url += `compartment=${compartment}&`;
      if (date) url += `date=${date}`;
      
      console.log('Fetching shape optimizations from:', url);
      const response = await api.get(url);
      console.log('Shape optimizations response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching shape optimizations:', error);
      throw error;
    }
  }
};

export default ociMonitoringApi;